import React, {useEffect} from 'react';
import Axios, {AxiosError, AxiosInstance} from 'axios';
import toast from 'react-hot-toast';
import {useAuth} from "../hooks/use-auth";
import {apiSettings} from '../config';
import {paths} from '../paths';

const NOT_AUTHENTICATED = 401;
export const ApiContext = React.createContext<IApiContext>({
  getClient:()=> {return undefined;}
});
ApiContext.displayName = 'API Context';

export const ApiProvider: React.FC<{ children: any }> = (props) => {
  const auth = useAuth();
  const [apiClient, setApiClient] = React.useState<AxiosInstance | undefined>();
  useEffect(() => {
    console.group('API Provider');
    if (auth.jwt) {
      console.log(`Creating client with JWT: ${auth.jwt}`);
      const client = Axios.create();
      client.defaults.headers["sessionid"] = `${auth.jwt}`;
      client.defaults.baseURL = apiSettings.baseUrl;
      client.interceptors.response.use(async (res) => {
        if(res.data.message) toast.success(res.data.message);
        return res;
      }, async (error) => {
        // check if unauthorized, trigger re-auth
        console.dir(error);
        const message = getFriendlyErrorMessage(error);
        if(error.response.status == NOT_AUTHENTICATED){
          /*
          // todo reauthenticate session JWT against server
          console.log(auth.isSignedIn);
          if (auth.isSignedIn && auth.reAuthJwt && notAtLogin()) {
            await auth.reAuthJwt();
          } else */{
            toast.error(message);
            const newUrl = `${paths.public.login.index}?redirect=${window.location.pathname}`;
            window.location.replace(newUrl);
          }
        } else if(isRepositoryError(error)){
          console.log('handle repository error');
          // don't DDOS your own server, 1-second delay
          return new Promise((resolve) => {
            setTimeout(() => {
              resolve(client(error.response.config));
            }, 1000);
          });
        }
        toast.error(message);
        return Promise.reject(error);
      })
      setApiClient(() => client);
    } else {
      console.log('Removing client');
      setApiClient(undefined);
    }
    console.groupEnd();
  }, [auth.jwt]);
  const value: IApiContext = React.useMemo(()=>({
    getClient: () => apiClient
  }), [apiClient]);

  return (
    <ApiContext.Provider value={value}>
      {props.children}
    </ApiContext.Provider>
  )

}

interface IApiContext {
  getClient(): AxiosInstance | undefined;
}

export function getFriendlyErrorMessage(err: AxiosError<{ message: string; name: string; }>): string {
  return err?.response?.data?.message || err.message;
}

function notAtLogin(): boolean {
  return [paths.public.login, paths.public.index].indexOf(window.location.pathname) === -1;
}

function isRepositoryError(error: AxiosError<{ message: string; name: string; }>): boolean {
  let isRepositoryError = false;
  if (error.isAxiosError) {
    if (error?.response?.data?.name?.toLowerCase() === 'repositorynotfounderror') {
      isRepositoryError = true;
    } else if (error?.response?.data?.message === '') {
      isRepositoryError = true;
    } else if (error?.response?.data?.message?.substring(0, 17).toLowerCase() === 'no repository for') {
      isRepositoryError = true;
    } else if (error?.response?.data?.message?.substring(0, 42) === 'DB Connection Error: Failed to connect to') {
      isRepositoryError = true;
    } else if (error?.response?.data?.message === "operation timed out for an unknown reason") {
      isRepositoryError = true
    }
  }
  return isRepositoryError;
}